import SignaturePad from "@/components/SignaturePad";
import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "sonner";
import { Plus, Trash, FilePdf, Upload, DownloadSimple, Warning, CheckCircle, XCircle, ShieldWarning, Gauge } from "@phosphor-icons/react";
import { api, apiError, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import RiskMatrix5x5 from "./RiskMatrix5x5";

const MONTHS_ES = ["ENERO","FEBRERO","MARZO","ABRIL","MAYO","JUNIO","JULIO","AGOSTO","SEPTIEMBRE","OCTUBRE","NOVIEMBRE","DICIEMBRE"];
const SEMANAS = ["S1", "S2", "S3", "S4"];
const LIGHT_CLS = {
  green: "bg-emerald-100 text-emerald-800 border-emerald-300",
  yellow: "bg-amber-100 text-amber-800 border-amber-300",
  red: "bg-red-100 text-red-800 border-red-300",
};

export default function NormativeAuditModule() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [tab, setTab] = useState("dashboard");
  const [plan, setPlan] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [comp, setComp] = useState(null);
  const [defaults, setDefaults] = useState([]);
  const [iperRows, setIperRows] = useState([]);
  const [bowtieRows, setBowtieRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, e, c, d, iper, bt] = await Promise.all([
        api.get(`/hse/audits-normativa/plan?year=${year}`),
        api.get(`/hse/audits-normativa/executions?year=${year}`),
        api.get(`/hse/audits-normativa/compliance?year=${year}`),
        api.get("/hse/audits-normativa/defaults"),
        api.get("/hse/iper").catch(() => ({ data: [] })),
        api.get("/hse/bowtie").catch(() => ({ data: [] })),
      ]);
      setPlan(p.data);
      setExecutions(e.data);
      setComp(c.data);
      setDefaults(d.data);
      setIperRows(iper.data || []);
      setBowtieRows(bt.data || []);
    } catch (ex) { toast.error(apiError(ex)); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const importXlsx = async (file, replace) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(
        `/hse/audits-normativa/plan/import-xlsx?year=${year}&replace=${replace ? "true" : "false"}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importadas ${data.inserted} filas · ignoradas ${data.skipped}`);
      load();
    } catch (ex) { toast.error(apiError(ex)); }
    finally { setBusy(false); }
  };

  const seedCatalog = async () => {
    if (!window.confirm(
      `¿Cargar el catálogo maestro SGSSO para ${year}?\n\n` +
      "13 NOMs · 28 actividades · 389 filas con checklist inline (derivado de la norma).\n" +
      "Esto REEMPLAZA cualquier plan existente del año."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/hse/audits-normativa/seed?year=${year}&replace=true`);
      toast.success(
        `Catálogo SGSSO cargado: ${data.inserted} filas · ${data.norms_seeded} NOMs · ` +
        `${data.critical_norms.length} críticas (${data.critical_norms.join(", ")})`
      );
      load();
    } catch (ex) { toast.error(apiError(ex)); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/hse/audits-normativa/executive-report/pdf?year=${year}`,
        { responseType: "blob" });
      downloadFile(r.data, `auditoria-normativa-ejecutivo-${year}.pdf`);
    } catch (ex) { toast.error(apiError(ex)); }
  };

  return (
    <div className="space-y-4" data-testid="hse-normative-audit">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading font-black text-xl text-slate-800">
            Gestión de Auditoría Normativa
          </h2>
          <p className="text-xs text-slate-500">Programa anual NOMs · Cumplimiento y alertas críticas</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Año</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28 h-9" data-testid="na-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) =>
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={downloadPdf}
                   className="h-9 gap-1.5 border-orange-300 text-orange-800 hover:bg-orange-50"
                   data-testid="na-pdf">
            <FilePdf size={14} weight="bold" /> Reporte Ejecutivo
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 gap-1">
          <TabsTrigger value="dashboard" data-testid="na-tab-dashboard">Cumplimiento</TabsTrigger>
          <TabsTrigger value="plan" data-testid="na-tab-plan">Plan ({plan.length})</TabsTrigger>
          <TabsTrigger value="exec" data-testid="na-tab-exec">Ejecución</TabsTrigger>
          <TabsTrigger value="defaults" data-testid="na-tab-defaults">Responsables</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <ComplianceTab comp={comp} iperRows={iperRows} bowtieRows={bowtieRows} />
        </TabsContent>

        <TabsContent value="plan">
          <PlanTab plan={plan} year={year} busy={busy}
                    onImport={importXlsx} onReload={load} />
        </TabsContent>

        <TabsContent value="exec">
          <ExecutionTab plan={plan} executions={executions}
                         defaults={defaults} year={year} onReload={load} />
        </TabsContent>

        <TabsContent value="defaults">
          <DefaultsTab defaults={defaults} onReload={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// -------- Sub-tab Cumplimiento --------
function ComplianceTab({ comp, iperRows = [], bowtieRows = [] }) {
  if (!comp) return <p className="text-slate-400 italic text-center py-8">Cargando…</p>;
  const t = comp.totals || {};
  const tcls = LIGHT_CLS[t.light] || LIGHT_CLS.red;
  return (
    <div className="space-y-4">
      {/* KPI hero */}
      <div className={`border-2 rounded-lg p-4 grid grid-cols-4 gap-3 ${tcls}`}
            data-testid="na-comp-hero">
        <Kpi label="Planeadas" value={t.planeadas} />
        <Kpi label="Cumplidas" value={t.cumplidas} />
        <Kpi label="% Anual" value={`${t.pct_anual || 0}%`} big />
        <Kpi label="Semáforo" value={(t.light || "?").toUpperCase()} big />
      </div>

      {/* Matriz 5x5 clickeable */}
      <RiskMatrix5x5 iperRows={iperRows} bowtieRows={bowtieRows} />

      {/* Alertas */}
      {comp.alerts?.length > 0 && (
        <div className="border-2 border-red-300 bg-red-50 rounded-lg p-3 space-y-1"
             data-testid="na-alerts">
          <p className="font-heading font-black text-red-900 flex items-center gap-1.5">
            <ShieldWarning size={16} weight="fill" /> Alertas del auditor
          </p>
          <ul className="text-sm text-red-800 space-y-1">
            {comp.alerts.map((a, i) => (
              <li key={i} className="flex gap-2">
                <span className="font-mono font-bold text-xs bg-red-200 px-1.5 py-0.5 rounded">
                  {a.level}
                </span>
                <span>{a.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Semáforo por Norma */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs" data-testid="na-comp-by-norma">
          <thead className="bg-slate-100">
            <tr className="text-left text-slate-500 uppercase tracking-wider">
              <th className="px-3 py-2 font-heading">Norma</th>
              <th className="px-3 py-2 font-heading text-right">Planeadas</th>
              <th className="px-3 py-2 font-heading text-right">Cumplidas</th>
              <th className="px-3 py-2 font-heading text-right">Pendientes</th>
              <th className="px-3 py-2 font-heading text-right">Vencidas</th>
              <th className="px-3 py-2 font-heading text-right">Sin Prog.</th>
              <th className="px-3 py-2 font-heading text-right">% Anual</th>
              <th className="px-3 py-2 font-heading text-center">Semáforo</th>
            </tr>
          </thead>
          <tbody>
            {(comp.by_norma || []).map((n) => (
              <tr key={n.norma} className="border-b border-slate-100"
                  data-testid={`na-norma-row-${n.norma}`}>
                <td className="px-3 py-2 font-mono font-bold text-slate-800">
                  {n.norma}{n.is_critical && <span className="ml-1 text-red-600 text-[10px]">CRÍTICA</span>}
                </td>
                <td className="px-3 py-2 text-right">{n.planeadas}</td>
                <td className="px-3 py-2 text-right text-emerald-700 font-bold">{n.cumplidas}</td>
                <td className="px-3 py-2 text-right text-amber-700">{n.pendientes}</td>
                <td className="px-3 py-2 text-right text-red-700 font-bold">{n.vencidas}</td>
                <td className="px-3 py-2 text-right text-slate-500">{n.sin_programacion}</td>
                <td className="px-3 py-2 text-right font-bold">{n.pct_anual}%</td>
                <td className="px-3 py-2 text-center">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${LIGHT_CLS[n.light]}`}>
                    {n.light?.toUpperCase()}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Por Mes */}
      <div className="bg-white border border-slate-200 rounded-lg p-3">
        <p className="font-heading font-black text-sm text-slate-800 mb-2">Cumplimiento por Mes</p>
        <div className="grid grid-cols-6 gap-2">
          {(comp.by_month || []).map((m) => (
            <div key={m.mes} className={`border rounded p-2 text-center ${LIGHT_CLS[m.light]}`}>
              <p className="text-[10px] uppercase font-bold">{m.mes_label.slice(0, 3)}</p>
              <p className="text-lg font-black">{m.pct}%</p>
              <p className="text-[10px]">{m.cumplidas}/{m.planeadas}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, big }) {
  return (
    <div>
      <p className="text-[10px] uppercase font-bold opacity-70">{label}</p>
      <p className={`${big ? "text-2xl" : "text-lg"} font-black font-heading`}>{value ?? 0}</p>
    </div>
  );
}

// -------- Sub-tab Plan --------
function PlanTab({ plan, year, busy, onImport, onReload, onSeed }) {
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState({});   // {id: bool}
  const [manual, setManual] = useState(null);     // form model | null
  const rows = useMemo(
    () => plan.filter((p) => !filter || `${p.norma} ${p.actividad}`.toLowerCase().includes(filter.toLowerCase())),
    [plan, filter],
  );
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const openManual = () => setManual({
    norma: "", actividad: "", mes: new Date().getMonth() + 1,
    semana: "", cantidad: 1, responsable_default: "", year: Number(year),
    notes: "", checklist_items: [],
  });

  const submitManual = async () => {
    if (!manual.norma || !manual.actividad) {
      toast.error("Norma y actividad son obligatorios"); return;
    }
    try {
      await api.post("/hse/audits-normativa/plan", {
        ...manual, semana: manual.semana || null,
      });
      toast.success("Fila agregada al plan");
      setManual(null); onReload();
    } catch (ex) { toast.error(apiError(ex)); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/hse/audits-normativa/plan/template.xlsx",
                                { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "plantilla-plan-auditoria-normativa.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Plantilla descargada");
    } catch (ex) { toast.error(apiError(ex)); }
  };
  return (
    <div className="space-y-3">
      <div className="bg-white border border-slate-200 rounded p-3 flex flex-wrap items-end gap-2">
        <Input placeholder="Buscar por norma o actividad…" value={filter}
                onChange={(e) => setFilter(e.target.value)} className="max-w-xs"
                data-testid="na-plan-filter" />
        <div className="ml-auto flex flex-wrap gap-2 items-end">
          <Button onClick={onSeed} disabled={busy}
                   className="h-9 gap-1.5 bg-emerald-700 hover:bg-emerald-600 text-white"
                   data-testid="na-plan-seed-catalog"
                   title="Carga las 13 NOMs · 28 actividades · checklists derivados de la norma">
            <ShieldWarning size={14} weight="fill" /> Cargar catálogo SGSSO ({year})
          </Button>
          <Button onClick={openManual} disabled={busy}
                   variant="outline"
                   className="h-9 gap-1.5 border-blue-300 text-blue-800 hover:bg-blue-50"
                   data-testid="na-plan-add-manual">
            <Plus size={14} weight="bold" /> Añadir manual
          </Button>
          <Button onClick={downloadTemplate} disabled={busy}
                   variant="outline"
                   className="h-9 gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-50"
                   data-testid="na-plan-download-template">
            <DownloadSimple size={14} weight="bold" /> Plantilla Excel
          </Button>
          <div>
            <Label className="overline-label">o importa tu Excel</Label>
            <Input type="file" accept=".xlsx,.xlsm" disabled={busy}
                    onChange={(e) => e.target.files?.[0] && onImport(e.target.files[0], false)}
                    data-testid="na-plan-import"
                    className="w-64 file:bg-amber-100 file:text-amber-800 file:border-0 file:mr-2 file:px-2 file:py-1 file:rounded" />
          </div>
          <Button variant="outline" disabled={busy}
                   onClick={() => document.querySelector('[data-testid="na-plan-import-replace"]')?.click()}
                   className="h-9 border-red-300 text-red-800 hover:bg-red-50"
                   data-testid="na-plan-import-replace-btn">
            Reemplazar {year}
          </Button>
          <input type="file" accept=".xlsx,.xlsm" style={{ display: "none" }}
                  data-testid="na-plan-import-replace"
                  onChange={(e) => e.target.files?.[0] && window.confirm(`¿Borrar el plan ${year} completo y reimportar?`) && onImport(e.target.files[0], true)} />
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100">
            <tr className="text-left text-slate-500 uppercase tracking-wider">
              <th className="px-3 py-2 font-heading">Norma</th>
              <th className="px-3 py-2 font-heading">Actividad</th>
              <th className="px-3 py-2 font-heading">Mes</th>
              <th className="px-3 py-2 font-heading">Semana</th>
              <th className="px-3 py-2 font-heading text-right">Cant.</th>
              <th className="px-3 py-2 font-heading">Responsable</th>
              <th className="px-3 py-2 font-heading text-center">Checklist</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const items = p.checklist_items || [];
              const isOpen = !!expanded[p.id];
              return (
                <Fragment key={p.id}>
                  <tr className={`border-b border-slate-100 hover:bg-slate-50 ${p.critical ? "bg-red-50/40" : ""}`}
                      data-testid={`na-plan-row-${p.id}`}>
                    <td className="px-3 py-2 font-mono font-bold text-slate-800">
                      {p.norma}
                      {p.critical && <span className="ml-1 text-[9px] text-red-700 font-bold">◉ CRÍTICA</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{p.actividad}</td>
                    <td className="px-3 py-2 text-slate-600">{MONTHS_ES[p.mes - 1]}</td>
                    <td className="px-3 py-2">
                      {p.semana ? (
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-[10px] font-bold">
                          {p.semana}
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-bold" title="Sin programación semanal">
                          SIN PROG
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-bold">{p.cantidad}</td>
                    <td className="px-3 py-2 text-slate-500 text-[11px]">{p.responsable_default || "—"}</td>
                    <td className="px-3 py-2 text-center">
                      {items.length > 0 ? (
                        <button onClick={() => toggle(p.id)}
                                data-testid={`na-plan-checklist-toggle-${p.id}`}
                                className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold hover:bg-emerald-200">
                          {isOpen ? "▲ ocultar" : `▼ ${items.length} ítems`}
                        </button>
                      ) : (
                        <span className="text-slate-300 text-[11px]">—</span>
                      )}
                    </td>
                  </tr>
                  {isOpen && items.length > 0 && (
                    <tr data-testid={`na-plan-checklist-${p.id}`}>
                      <td colSpan={7} className="px-6 py-3 bg-emerald-50/60 border-b border-emerald-200">
                        <p className="text-[10px] uppercase font-bold text-emerald-800 mb-1.5">
                          Checklist según la norma {p.norma_name ? `· ${p.norma_name.split("·").slice(1).join("·").trim()}` : ""}
                        </p>
                        <ul className="text-[12px] text-slate-700 space-y-1">
                          {items.map((it, i) => (
                            <li key={i} className="flex gap-2">
                              <span className="text-emerald-700 font-bold">☐</span>
                              <span>{it}</span>
                            </li>
                          ))}
                        </ul>
                        {p.law && (
                          <p className="text-[10px] text-slate-500 italic mt-2">{p.law}</p>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-6 text-slate-400 italic">
                Plan vacío. Presiona <b>Cargar catálogo SGSSO</b> para arrancar (13 NOMs · 389 filas con checklist).
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ---------- Dialog Añadir manual ---------- */}
      <Dialog open={!!manual} onOpenChange={(v) => !v && setManual(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="na-plan-manual-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Añadir fila al plan · {year}</DialogTitle>
          </DialogHeader>
          {manual && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline-label">Norma *</Label>
                  <Input value={manual.norma}
                          onChange={(e) => setManual({ ...manual, norma: e.target.value })}
                          placeholder="Ej: NOM-002"
                          data-testid="na-plan-manual-norma" />
                </div>
                <div>
                  <Label className="overline-label">Cantidad</Label>
                  <Input type="number" min={1} value={manual.cantidad}
                          onChange={(e) => setManual({ ...manual, cantidad: Number(e.target.value) || 1 })}
                          data-testid="na-plan-manual-cantidad" />
                </div>
              </div>
              <div>
                <Label className="overline-label">Actividad *</Label>
                <Input value={manual.actividad}
                        onChange={(e) => setManual({ ...manual, actividad: e.target.value })}
                        placeholder="Ej: REVISION DE EXTINTORES"
                        data-testid="na-plan-manual-actividad" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline-label">Mes *</Label>
                  <select className="w-full h-9 rounded border border-slate-300 px-2 text-sm"
                           value={manual.mes}
                           onChange={(e) => setManual({ ...manual, mes: Number(e.target.value) })}
                           data-testid="na-plan-manual-mes">
                    {MONTHS_ES.map((m, i) => (
                      <option key={i} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="overline-label">Semana</Label>
                  <select className="w-full h-9 rounded border border-slate-300 px-2 text-sm"
                           value={manual.semana || ""}
                           onChange={(e) => setManual({ ...manual, semana: e.target.value })}
                           data-testid="na-plan-manual-semana">
                    <option value="">(sin semana)</option>
                    {["S1", "S2", "S3", "S4"].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label className="overline-label">Responsable por defecto (opcional)</Label>
                <Input value={manual.responsable_default}
                        onChange={(e) => setManual({ ...manual, responsable_default: e.target.value })}
                        placeholder="Ej: Ing. Ventilación"
                        data-testid="na-plan-manual-responsable" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setManual(null)}>Cancelar</Button>
            <Button onClick={submitManual} className="bg-blue-900 hover:bg-blue-800"
                    data-testid="na-plan-manual-save">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Sub-tab Ejecución --------
function ExecutionTab({ plan, executions, defaults, year, onReload }) {
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(emptyExec(year));
  const [busy, setBusy] = useState(false);

  const defaultsMap = useMemo(
    () => Object.fromEntries((defaults || []).map((d) => [d.norma, d.responsable_default])),
    [defaults],
  );

  // Cuando cambia la norma o actividad, buscar el plan matching y precargar
  // el checklist (items del catálogo SGSSO). Sólo si aún no hay resultados
  // — para no borrar lo que el auditor ya empezó a marcar.
  const loadChecklistFromPlan = (norma, actividad) => {
    if (!norma || !actividad) return;
    const match = plan.find((p) => p.norma === norma && p.actividad === actividad);
    const items = match?.checklist_items || [];
    if (!items.length) return;
    setForm((f) => {
      const currentResults = f.checklist_results || [];
      // Si ya hay resultados capturados para esta actividad, no sobreescribir
      if (currentResults.length > 0 && currentResults[0]?.item === items[0]) return f;
      return {
        ...f,
        plan_id: match?.id || null,
        mes: match?.mes || f.mes,
        semana: match?.semana || f.semana,
        checklist_results: items.map((it) => ({
          item: it, status: "pending", note: "", photo: "",
        })),
      };
    });
  };

  // Cuando cambia la norma, precarga el responsable default
  const changeNorma = (norma) => {
    setForm((f) => ({
      ...f, norma,
      action_owner: f.action_owner || defaultsMap[norma] || "",
    }));
    loadChecklistFromPlan(norma, form.actividad);
  };

  const changeActividad = (actividad) => {
    setForm((f) => ({ ...f, actividad }));
    loadChecklistFromPlan(form.norma, actividad);
  };

  const patchResult = (idx, patch) => {
    setForm((f) => ({
      ...f,
      checklist_results: (f.checklist_results || []).map((r, i) =>
        i === idx ? { ...r, ...patch } : r),
    }));
  };

  const handlePhotoUpload = (idx, file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("La foto excede 3 MB"); return;
    }
    const reader = new FileReader();
    reader.onload = (e) => patchResult(idx, { photo: e.target.result });
    reader.readAsDataURL(file);
  };

  // Contadores para el header del checklist
  const checklistStats = useMemo(() => {
    const rs = form.checklist_results || [];
    const cumple = rs.filter((r) => r.status === "cumple").length;
    const noCumple = rs.filter((r) => r.status === "no_cumple").length;
    const na = rs.filter((r) => r.status === "na").length;
    const pending = rs.length - cumple - noCumple - na;
    return { total: rs.length, cumple, noCumple, na, pending };
  }, [form.checklist_results]);

  // Auto-sync: cuando el checklist detecta >=1 no_cumple, forzar hallazgo=true
  // en el estado del form para que el panel de OCA (owner/due/priority)
  // aparezca automáticamente. Servidor igualmente lo fuerza, pero así el
  // auditor puede capturar los campos de la OCA desde el UI.
  useEffect(() => {
    if (checklistStats.noCumple > 0 && !form.hallazgo) {
      setForm((f) => ({ ...f, hallazgo: true }));
    }
  }, [checklistStats.noCumple, form.hallazgo]);

  // Actividades disponibles para la norma seleccionada (del plan)
  const actividadesForNorma = useMemo(() => {
    if (!form.norma) return [];
    return [...new Set(plan.filter((p) => p.norma === form.norma).map((p) => p.actividad))].sort();
  }, [plan, form.norma]);

  const submit = async () => {
    if (!form.norma || !form.actividad || !form.fecha_real) {
      toast.error("Norma, actividad y fecha real son obligatorias"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/hse/audits-normativa/executions", {
        ...form,
        year: Number(year),
        mes: Number(form.mes),
      });
      toast.success(
        form.hallazgo
          ? `Ejecución registrada + OCA #${(data.oca_id || "").slice(0, 8)} creada`
          : "Ejecución registrada",
      );
      setDlg(false);
      setForm(emptyExec(year));
      onReload();
    } catch (ex) { toast.error(apiError(ex)); }
    finally { setBusy(false); }
  };

  const delExec = async (id) => {
    if (!window.confirm("¿Eliminar esta ejecución? La OCA vinculada se mantiene.")) return;
    try {
      await api.delete(`/hse/audits-normativa/executions/${id}`);
      onReload();
    } catch (ex) { toast.error(apiError(ex)); }
  };

  const uniqNormas = [...new Set(plan.map((p) => p.norma))].sort();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">{executions.length} ejecuciones registradas en {year}</p>
        <Button onClick={() => setDlg(true)} className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                 data-testid="na-exec-new">
          <Plus size={13} weight="bold" /> Registrar ejecución
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100">
            <tr className="text-left text-slate-500 uppercase tracking-wider">
              <th className="px-3 py-2">Fecha real</th>
              <th className="px-3 py-2">Norma</th>
              <th className="px-3 py-2">Actividad</th>
              <th className="px-3 py-2">Mes/Sem</th>
              <th className="px-3 py-2">Folio</th>
              <th className="px-3 py-2">Hallazgo</th>
              <th className="px-3 py-2">OCA</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {executions.map((e) => (
              <tr key={e.id} className="border-b border-slate-100"
                   data-testid={`na-exec-row-${e.id}`}>
                <td className="px-3 py-2 font-mono">{e.fecha_real}</td>
                <td className="px-3 py-2 font-mono font-bold">{e.norma}</td>
                <td className="px-3 py-2 max-w-[280px] truncate" title={e.actividad}>{e.actividad}</td>
                <td className="px-3 py-2">{MONTHS_ES[e.mes - 1]?.slice(0, 3)} · {e.semana || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{e.folio_evidencia || "—"}</td>
                <td className="px-3 py-2">
                  {e.hallazgo
                    ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-800">SÍ</span>
                    : <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">NO</span>}
                </td>
                <td className="px-3 py-2 font-mono text-[10px] text-blue-800">
                  {e.oca_id ? `#${e.oca_id.slice(0, 8)}` : "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => delExec(e.id)}
                           className="text-red-600 hover:text-red-800"
                           data-testid={`na-exec-del-${e.id}`}>
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {executions.length === 0 && (
              <tr><td colSpan={8} className="text-center py-6 text-slate-400 italic">
                Sin ejecuciones registradas aún.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white max-w-xl" data-testid="na-exec-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Registrar ejecución de auditoría</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline-label">Norma *</Label>
                <Select value={form.norma} onValueChange={changeNorma}>
                  <SelectTrigger data-testid="na-exec-norma"><SelectValue placeholder="Selecciona norma" /></SelectTrigger>
                  <SelectContent>
                    {uniqNormas.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline-label">Fecha real *</Label>
                <Input type="date" value={form.fecha_real}
                        onChange={(e) => setForm({ ...form, fecha_real: e.target.value })}
                        data-testid="na-exec-fecha" />
              </div>
            </div>
            <div>
              <Label className="overline-label">Actividad *</Label>
              {actividadesForNorma.length > 0 ? (
                <Select value={form.actividad} onValueChange={changeActividad}>
                  <SelectTrigger data-testid="na-exec-actividad-select">
                    <SelectValue placeholder="Selecciona actividad del plan" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72 bg-white">
                    {actividadesForNorma.map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.actividad}
                        onChange={(e) => changeActividad(e.target.value)}
                        placeholder="Ej: REVISION DE EXTINTORES"
                        data-testid="na-exec-actividad" />
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="overline-label">Mes</Label>
                <Select value={String(form.mes)} onValueChange={(v) => setForm({ ...form, mes: Number(v) })}>
                  <SelectTrigger data-testid="na-exec-mes"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS_ES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline-label">Semana</Label>
                <Select value={form.semana || ""} onValueChange={(v) => setForm({ ...form, semana: v || null })}>
                  <SelectTrigger data-testid="na-exec-sem"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEMANAS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline-label">Folio evidencia</Label>
                <Input value={form.folio_evidencia}
                        onChange={(e) => setForm({ ...form, folio_evidencia: e.target.value })}
                        placeholder="Ej: AUD-2026-045"
                        data-testid="na-exec-folio" />
              </div>
            </div>
            <div>
              <Label className="overline-label">Responsable operativo</Label>
              <Input value={form.responsable}
                      onChange={(e) => setForm({ ...form, responsable: e.target.value })}
                      placeholder="Quien ejecutó la auditoría"
                      data-testid="na-exec-responsable" />
            </div>

            {/* ---------- CHECKLIST INTERACTIVO ---------- */}
            {(form.checklist_results || []).length > 0 && (
              <div className="border-2 border-emerald-200 bg-emerald-50/40 rounded-lg p-3 space-y-2"
                   data-testid="na-exec-checklist">
                <div className="flex items-center justify-between">
                  <p className="font-heading font-black text-sm text-emerald-900 flex items-center gap-1.5">
                    <CheckCircle size={16} weight="fill" /> Checklist según la norma
                  </p>
                  <div className="flex gap-1.5 text-[10px]">
                    <span className="px-1.5 py-0.5 rounded font-bold bg-emerald-100 text-emerald-800 border border-emerald-300"
                          data-testid="na-checklist-count-cumple">
                      ✓ {checklistStats.cumple}
                    </span>
                    <span className="px-1.5 py-0.5 rounded font-bold bg-red-100 text-red-800 border border-red-300"
                          data-testid="na-checklist-count-nocumple">
                      ✗ {checklistStats.noCumple}
                    </span>
                    <span className="px-1.5 py-0.5 rounded font-bold bg-slate-200 text-slate-700 border border-slate-300"
                          data-testid="na-checklist-count-na">
                      N/A {checklistStats.na}
                    </span>
                    <span className="px-1.5 py-0.5 rounded font-bold bg-amber-100 text-amber-800 border border-amber-300"
                          data-testid="na-checklist-count-pending">
                      ⏳ {checklistStats.pending}
                    </span>
                  </div>
                </div>
                {checklistStats.noCumple > 0 && (
                  <p className="text-[11px] text-red-700 font-bold bg-red-50 border border-red-200 rounded px-2 py-1"
                     data-testid="na-checklist-warn-auto">
                    ⚠ {checklistStats.noCumple} ítem(s) NO CUMPLE — se creará OCA automáticamente al guardar.
                  </p>
                )}
                <ol className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {form.checklist_results.map((r, idx) => (
                    <li key={idx}
                        className={`bg-white rounded border p-2 ${
                          r.status === "cumple" ? "border-emerald-300" :
                          r.status === "no_cumple" ? "border-red-300" :
                          r.status === "na" ? "border-slate-300" : "border-slate-200"
                        }`}
                        data-testid={`na-checklist-item-${idx}`}>
                      <div className="flex items-start gap-2">
                        <span className="text-[10px] font-mono font-bold text-slate-400 mt-0.5">{idx + 1}.</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] text-slate-800 leading-snug">{r.item}</p>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {[
                              { v: "cumple", l: "Cumple", cls: "bg-emerald-600 border-emerald-700" },
                              { v: "no_cumple", l: "No cumple", cls: "bg-red-600 border-red-700" },
                              { v: "na", l: "N/A", cls: "bg-slate-500 border-slate-600" },
                            ].map((opt) => (
                              <button
                                key={opt.v}
                                type="button"
                                onClick={() => patchResult(idx, { status: opt.v })}
                                data-testid={`na-checklist-${idx}-${opt.v}`}
                                className={`text-[10px] px-2 py-0.5 rounded border font-bold uppercase tracking-wide transition ${
                                  r.status === opt.v
                                    ? `${opt.cls} text-white`
                                    : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                                }`}>
                                {opt.l}
                              </button>
                            ))}
                            {r.status !== "pending" && (
                              <button type="button" onClick={() => patchResult(idx, { status: "pending" })}
                                       className="text-[10px] text-slate-400 hover:text-slate-600 underline ml-1">
                                limpiar
                              </button>
                            )}
                          </div>
                          {r.status === "no_cumple" && (
                            <div className="mt-1.5 space-y-1">
                              <Textarea rows={1} value={r.note}
                                        onChange={(e) => patchResult(idx, { note: e.target.value })}
                                        placeholder="Detalle del incumplimiento (opcional)…"
                                        className="text-[11px] py-1 min-h-0"
                                        data-testid={`na-checklist-${idx}-note`} />
                              <label className="text-[11px] text-slate-600 flex items-center gap-2 cursor-pointer">
                                <input type="file" accept="image/*" className="hidden"
                                       onChange={(e) => handlePhotoUpload(idx, e.target.files?.[0])}
                                       data-testid={`na-checklist-${idx}-photo-input`} />
                                <span className="px-2 py-0.5 rounded border border-red-300 text-red-700 hover:bg-red-50 font-bold">
                                  📷 {r.photo ? "Cambiar foto" : "Adjuntar foto"}
                                </span>
                                {r.photo && (
                                  <>
                                    <img src={r.photo} alt="evidencia"
                                         className="h-8 w-8 object-cover rounded border border-slate-300" />
                                    <button type="button" onClick={() => patchResult(idx, { photo: "" })}
                                            className="text-red-500 hover:text-red-700 text-[10px]">
                                      quitar
                                    </button>
                                  </>
                                )}
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* ---------- FIRMA DEL AUDITOR (al final del checklist) ---------- */}
            {(form.checklist_results || []).length > 0 && (
              <div className="border-2 border-blue-200 bg-blue-50/40 rounded-lg p-3 space-y-2"
                   data-testid="na-exec-signature-block">
                <p className="font-heading font-black text-sm text-blue-900 flex items-center gap-1.5">
                  ✍ Firma del auditor
                </p>
                <p className="text-[11px] text-slate-600">
                  Al firmar das validez al informe de auditoría y confirmas que los resultados
                  del checklist son verídicos y verificables en campo.
                </p>
                <div className="grid md:grid-cols-2 gap-3 items-start">
                  <div>
                    <Label className="overline-label">Nombre del auditor</Label>
                    <Input value={form.auditor_name || ""}
                            onChange={(e) => setForm({ ...form, auditor_name: e.target.value })}
                            placeholder="Ej: Ing. Juan Pérez · SGSSO"
                            data-testid="na-exec-auditor-name" />
                  </div>
                  <div>
                    <Label className="overline-label">Firma digital</Label>
                    <SignaturePad
                      value={form.auditor_signature || ""}
                      onChange={(sig) => setForm({ ...form, auditor_signature: sig })}
                      width={420} height={110}
                      testid="na-exec-auditor-signature" />
                  </div>
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 border-t border-slate-200 pt-3 cursor-pointer">
              <input type="checkbox" checked={form.hallazgo}
                      onChange={(e) => setForm({ ...form, hallazgo: e.target.checked })}
                      data-testid="na-exec-hallazgo" />
              <span className="text-sm font-bold text-red-700">¿Hubo hallazgo o no conformidad?
              {checklistStats.noCumple > 0 && (
                <span className="ml-2 text-[10px] text-red-600 font-normal italic">
                  (auto-marcado por checklist)
                </span>
              )}
              </span>
            </label>

            {form.hallazgo && (
              <div className="bg-red-50 border border-red-200 rounded p-3 space-y-2">
                <p className="text-[11px] font-bold text-red-700">
                  Al guardar se creará automáticamente una <b>OCA</b> en Acciones Correctivas.
                </p>
                <div>
                  <Label className="overline-label">Descripción del hallazgo *</Label>
                  <Textarea rows={2} value={form.hallazgo_desc}
                            onChange={(e) => setForm({ ...form, hallazgo_desc: e.target.value })}
                            placeholder="Ej: 3 extintores vencidos, cabezal roto…"
                            data-testid="na-exec-hallazgo-desc" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="overline-label">Responsable OCA *</Label>
                    <Input value={form.action_owner}
                            onChange={(e) => setForm({ ...form, action_owner: e.target.value })}
                            placeholder={defaultsMap[form.norma] || "Asignar..."}
                            data-testid="na-exec-owner" />
                    {defaultsMap[form.norma] && !form.action_owner && (
                      <p className="text-[10px] text-slate-500 mt-0.5">Default: {defaultsMap[form.norma]}</p>
                    )}
                  </div>
                  <div>
                    <Label className="overline-label">Fecha compromiso</Label>
                    <Input type="date" value={form.action_due_date}
                            onChange={(e) => setForm({ ...form, action_due_date: e.target.value })}
                            data-testid="na-exec-due" />
                  </div>
                  <div>
                    <Label className="overline-label">Prioridad</Label>
                    <Select value={form.action_priority}
                             onValueChange={(v) => setForm({ ...form, action_priority: v })}>
                      <SelectTrigger data-testid="na-exec-prio"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Baja</SelectItem>
                        <SelectItem value="medium">Media</SelectItem>
                        <SelectItem value="high">Alta</SelectItem>
                        <SelectItem value="critical">Crítica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy || !form.norma || !form.actividad || !form.fecha_real}
                     className="bg-blue-900 hover:bg-blue-800"
                     data-testid="na-exec-save">
              {busy ? "Guardando…" : (form.hallazgo ? "Guardar + Crear OCA" : "Guardar ejecución")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const emptyExec = (year) => ({
  plan_id: null, norma: "", actividad: "",
  year: Number(year), mes: new Date().getMonth() + 1, semana: null,
  fecha_real: new Date().toISOString().slice(0, 10),
  folio_evidencia: "", hallazgo: false, hallazgo_desc: "",
  responsable: "", action_owner: "", action_due_date: "",
  action_priority: "medium", evidence_photos: [],
  checklist_results: [],
  auditor_signature: "", auditor_name: "",
});

// -------- Sub-tab Defaults (responsables por norma) --------
function DefaultsTab({ defaults, onReload }) {
  const [norma, setNorma] = useState("");
  const [resp, setResp] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!norma.trim() || !resp.trim()) { toast.error("Norma y responsable requeridos"); return; }
    setBusy(true);
    try {
      await api.put("/hse/audits-normativa/defaults", {
        norma: norma.trim().replace(/\s+/g, ""),
        responsable_default: resp.trim(),
      });
      setNorma(""); setResp("");
      toast.success("Default guardado");
      onReload();
    } catch (ex) { toast.error(apiError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="bg-white border border-slate-200 rounded p-3">
        <p className="text-xs text-slate-600 mb-2">
          Configura un responsable por defecto por Norma. Cuando registres un hallazgo,
          el responsable de la OCA se autofill con este valor (siempre editable).
        </p>
        <div className="grid grid-cols-3 gap-2 items-end">
          <div>
            <Label className="overline-label">Norma</Label>
            <Input value={norma} onChange={(e) => setNorma(e.target.value)}
                    placeholder="Ej: NOM-010"
                    data-testid="na-def-norma" />
          </div>
          <div>
            <Label className="overline-label">Responsable default</Label>
            <Input value={resp} onChange={(e) => setResp(e.target.value)}
                    placeholder="Ej: Ing. Higiene Industrial"
                    data-testid="na-def-resp" />
          </div>
          <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-800"
                   data-testid="na-def-save">
            Guardar / Actualizar
          </Button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-100">
            <tr className="text-left text-slate-500 uppercase tracking-wider">
              <th className="px-3 py-2">Norma</th>
              <th className="px-3 py-2">Responsable default</th>
              <th className="px-3 py-2">Actualizado</th>
            </tr>
          </thead>
          <tbody>
            {defaults.map((d) => (
              <tr key={d.norma} className="border-b border-slate-100" data-testid={`na-def-row-${d.norma}`}>
                <td className="px-3 py-2 font-mono font-bold">{d.norma}</td>
                <td className="px-3 py-2">{d.responsable_default}</td>
                <td className="px-3 py-2 text-slate-500 text-[11px]">
                  {(d.updated_at || "").slice(0, 16).replace("T", " ")} · {d.updated_by || ""}
                </td>
              </tr>
            ))}
            {defaults.length === 0 && (
              <tr><td colSpan={3} className="text-center py-4 text-slate-400 italic">
                Sin defaults capturados aún.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
